import {InjectionToken,Injectable,PLATFORM_ID,computed,inject,signal} from '@angular/core';
import {DOCUMENT,isPlatformBrowser} from '@angular/common';

/** Bump to re-ask everyone — a stored record below this is treated as undecided. */
export const CONSENT_VERSION=1;

const STORAGE_KEY='app-cookie-consent';

export const CONSENT_STORAGE=new InjectionToken<Storage|null>('consent.storage',{
	providedIn:'root',
	factory:()=>{
		if(!isPlatformBrowser(inject(PLATFORM_ID))) return null;
		try{
			return inject(DOCUMENT).defaultView?.localStorage??null;
		}catch{
			return null;
		}
	}
});

export interface ConsentState{
	preferences:boolean;
	analytics:boolean;
	marketing:boolean;
	version:number;
	at:string;
}

@Injectable({providedIn:'root'})
export class ConsentService{
	private readonly storage=inject(CONSENT_STORAGE);

	private readonly stored=signal<ConsentState|null>(this.read());
	private readonly reopened=signal<boolean>(false);

	readonly state=this.stored.asReadonly();
	readonly decided=computed<boolean>(()=>this.stored()!==null);
	readonly bannerOpen=computed<boolean>(()=>!this.decided()||this.reopened());

	readonly preferences=computed<boolean>(()=>this.stored()?.preferences??false);
	readonly analytics=computed<boolean>(()=>this.stored()?.analytics??false);
	readonly marketing=computed<boolean>(()=>this.stored()?.marketing??false);

	private read():ConsentState|null{
		try{
			const raw=this.storage?.getItem(STORAGE_KEY);
			if(!raw) return null;
			const stored=JSON.parse(raw) as ConsentState;
			return stored.version===CONSENT_VERSION ? stored : null;
		}catch{
			return null;
		}
	}

	save(choice:{preferences:boolean;analytics:boolean;marketing:boolean}):void{
		const state:ConsentState={...choice,version:CONSENT_VERSION,at:new Date().toISOString()};
		try{
			this.storage?.setItem(STORAGE_KEY,JSON.stringify(state));
		}catch{
			// A rejected write must not lose the in-session choice.
		}
		this.stored.set(state);
		this.reopened.set(false);
	}

	acceptAll():void{
		this.save({preferences:true,analytics:true,marketing:true});
	}

	rejectAll():void{
		this.save({preferences:false,analytics:false,marketing:false});
	}

	reopen():void{
		this.reopened.set(true);
	}

	close():void{
		this.reopened.set(false);
	}
}
